import { Icon, TableHeading } from '@nike/eds'
import { flexRender, type HeaderGroup } from '@tanstack/react-table'
import { type RefObject } from 'react'

import { ColumnType, Filter } from './Filter'
import { type TableColumn } from './TableView'

interface TableHeaderProps {
  headerGroups: Array<HeaderGroup<any>>
  isSticky: boolean
  theadRef: RefObject<HTMLTableSectionElement>
  textFilterDelay?: number
  columnTypeMapping: Map<string, { type: ColumnType, enumValues?: string[] }>
  columns: TableColumn[]
}

/**
 * TableHeader component that handles rendering the table header section
 * Including column headings, sort indicators, and filter components
 */
export const TableHeader = ({
  headerGroups,
  isSticky,
  theadRef,
  textFilterDelay = 0,
  columnTypeMapping,
  columns
}: TableHeaderProps) => {
  return (
    <thead ref={theadRef} className={isSticky ? 'sticky top-0 border-b-gray-200 z-10' : ''}>
      {headerGroups.map(headerGroup => (
        <tr key={headerGroup.id} className={'eds-background--default'}>
          {headerGroup.headers.map(header => {
            return (
              <TableHeading
                className='!text-l h-full'
                key={header.id}
                colSpan={header.colSpan}
                style={{
                  minWidth: header.column.columnDef.size,
                  maxWidth: header.column.columnDef.size
                }}
              >
                <div
                  {...{
                    className: `flex flex-col justify-between h-full ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`,
                    onClick: header.column.getToggleSortingHandler()
                  }}
                >
                  <div className="flex-1">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: <Icon name='CaretUp' size='s' className='ml-2 mb-1' />,
                      desc: <Icon name='CaretDown' size='s' className='ml-2 mb-1' />
                    }[header.column.getIsSorted() as string] ?? null}
                  </div>
                  {
                    header.column.getCanFilter() && (
                      <Filter
                        column={header.column}
                        type={columnTypeMapping.get(header.column.id)?.type ?? ColumnType.TEXT}
                        textFilterDelay={textFilterDelay}
                        enumValues={columnTypeMapping.get(header.column.id)?.enumValues}
                        initialValue={columns.find(column => column.id === header.column.id)?.initialFilterValue}
                      />
                    )
                  }
                </div>
              </TableHeading>
            )
          })}
        </tr>
      ))}
    </thead>
  )
}
